import React from 'react';
import dynamic from 'next/dynamic';
import { String, Theme } from '@silvertours/front-shared';
import { useMedia } from 'react-use';
import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';
import { useSiteInfo } from '@silvertours/front-entities';
import Phone from '@engineering/icons/phone';
import { Schedule } from './Schedule';

const FamilyTripPictogram = dynamic(() =>
  import('./FamilyTripPictogram').then(module => module.FamilyTripPictogram),
);

const StyledFamilyTripPictogram = styled(FamilyTripPictogram)`
  display: block;
  width: 100%;
  height: auto;
  ${({ theme }) => theme.mq('md')} {
    margin-top: 1.5rem;
    width: calc(100% - 350px);
  }
  ${({ theme }) => theme.mq('lg')} {
    margin-top: 0;
    width: 100%;
  }
`;

const Wrapper = styled.section`
  display: block;
  margin: 1rem 0;
  padding: 2rem;
  border-radius: ${({ theme }) => theme.radius.md};
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 10px 25px rgb(0 0 0 / 8%);
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral.grey160};
  background-color: ${({ theme }) => theme.colors.neutral.white100};
  fill: currentcolor;

  ${({ theme }) => theme.mq('lg')} {
    margin-top: 0;
  }
`;

const Text = styled.span`
  display: block;
  font-size: 1.125rem;
  font-family: ${({ theme }) => theme.fontFamily.default};
`;

const PhoneNumber = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0 0;
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.xs};
  font: inherit;
  font-family: ${({ theme }) => theme.fontFamily.alternate};
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.brand.green120};
  background: none;
  fill: currentcolor;
  white-space: nowrap;
  cursor: pointer;

  &:hover span {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 0.125rem dashed ${({ theme }) => theme.colors.brand.orange100};
    outline-offset: 0.25rem;
  }
`;

const PhoneNumberIcon = styled(Phone)`
  fill: currentcolor;
  width: 1.5rem;
  height: 1.5rem;
`;

const Support = () => {
  const t = useTranslations('features.supportInfo');
  const {
    settings: { phoneNumber, language },
    featureToggles,
  } = useSiteInfo();

  const isMobile = useMedia(
    `(max-width: ${Theme.getBreakpoint('lg')}px)`,
    true,
  );

  const formattedNumber = String.parsePhoneNumber(phoneNumber, language);

  if (featureToggles.hideInfoline) {
    return <StyledFamilyTripPictogram />;
  }

  const handleClick = () => {
    window.location.assign(formattedNumber.url);
  };

  return (
    <Wrapper
      arial-label={isMobile ? t('contact.text') : undefined}
      aria-labelledby={isMobile ? undefined : 'footer-support-title'}
    >
      {!isMobile && <Text id="footer-support-title">{t('contact.text')}</Text>}
      <PhoneNumber type="button" onClick={handleClick}>
        <PhoneNumberIcon />
        <span role="presentation">{formattedNumber.national}</span>
      </PhoneNumber>
      {isMobile && <Schedule />}
    </Wrapper>
  );
};

export { Support };
